/**
 * SWMManpowerRollup — project-wide S-curve of daily manpower + equipment
 * summed across every tag with an APPROVED (or SUGGESTED-as-fallback) Cognara
 * resource plan. Reads /swm/cognara/manpower-rollup which does the aggregation
 * server-side so the browser only handles the presentation.
 */
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { X, Loader2, Users, HardHat, Wrench } from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  Tooltip, Legend, CartesianGrid,
} from 'recharts';

export default function SWMManpowerRollup({ projectId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('manpower'); // manpower | equipment

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/v1/projects/${projectId}/swm/cognara/manpower-rollup`);
        setData(data);
      } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load rollup'); }
      finally { setLoading(false); }
    })();
  }, [projectId]);

  const series = data?.series || [];
  const dailyKey = view === 'manpower' ? 'totalManpower' : 'totalEquipment';
  const cumKey   = view === 'manpower' ? 'cumulativeManpower' : 'cumulativeEquipment';
  const typeTotals = view === 'manpower' ? (data?.manpowerTypes || []) : (data?.equipmentTypes || []);
  const peak = series.reduce((m, r) => Math.max(m, r[dailyKey] || 0), 0);
  const peakDay = series.find(r => r[dailyKey] === peak);
  const totalPersonDays = series.reduce((s, r) => s + (r[dailyKey] || 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" style={{ background: 'rgba(10,14,26,0.65)' }} data-testid="swm-manpower-rollup">
      <div className="w-full max-w-[1200px] h-full flex flex-col shadow-2xl" style={{ background: 'var(--card-bg)' }}>
        <div className="flex items-center justify-between border-b p-4" style={{ borderColor: 'var(--border-color)' }}>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-mono" style={{ color: '#F26B1F' }}>Cognara · Manpower Rollup</div>
            <div className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Project-wide daily crew + equipment S-curve</div>
            <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
              {data?.tagsIncluded?.length || 0} tags rolled up · {series.length} working days · peak {peak} people on {peakDay?.date}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-black/10" data-testid="swm-rollup-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex border-b" style={{ borderColor: 'var(--border-color)' }}>
          {[{k:'manpower', l:'Manpower', I:HardHat}, {k:'equipment', l:'Equipment', I:Wrench}].map(t => {
            const active = view === t.k;
            const Icon = t.I;
            return (
              <button key={t.k} onClick={() => setView(t.k)}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-[11px] uppercase tracking-[0.12em] transition"
                style={{ background: active ? 'rgba(242,107,31,0.10)' : 'transparent',
                         color: active ? '#F26B1F' : 'var(--text-muted)',
                         borderBottom: active ? '2px solid #F26B1F' : '2px solid transparent' }}
                data-testid={`swm-rollup-tab-${t.k}`}>
                <Icon className="w-3.5 h-3.5" />{t.l}
              </button>
            );
          })}
        </div>
        <div className="flex-1 overflow-auto p-5 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#F26B1F' }} />
            </div>
          ) : series.length === 0 ? (
            <div className="text-center py-12 text-[12px] italic" style={{ color: 'var(--text-muted)' }}>
              No Cognara resource plans yet. Open a tag's Plan → Phase 4 → Run resource forecast, then refresh.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-4 gap-3">
                <StatCard label="Peak / day"   value={peak}          sub={peakDay?.date}  icon={Users} color="#F26B1F"/>
                <StatCard label="Total person-days" value={totalPersonDays.toLocaleString()} sub={view === 'manpower' ? 'sum across window' : 'equipment-days'} icon={HardHat} color="#143A6B"/>
                <StatCard label="Working days" value={series.length}  sub="excl. weekends" icon={Wrench} color="#0E7C6B"/>
                <StatCard label="Tags rolled up" value={data?.tagsIncluded?.length || 0} sub="approved / suggested" icon={Users} color="#F59206"/>
              </div>

              <div className="rounded-xl border p-3" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg-alt)' }}>
                <div className="text-[11px] uppercase tracking-[0.12em] mb-2" style={{ color: 'var(--text-muted)' }}>
                  Daily load + cumulative S-curve
                </div>
                <div style={{ width: '100%', height: 340 }}>
                  <ResponsiveContainer>
                    <ComposedChart data={series} margin={{ top: 10, right: 40, left: 10, bottom: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" />
                      <XAxis dataKey="date" tick={{ fill: 'var(--text-muted)', fontSize: 9 }} angle={-30} textAnchor="end" height={50}/>
                      <YAxis yAxisId="left"  tick={{ fill: 'var(--text-muted)', fontSize: 9 }} label={{ value: 'Per-day', angle: -90, position: 'insideLeft', fill: 'var(--text-muted)', fontSize: 10 }}/>
                      <YAxis yAxisId="right" orientation="right" tick={{ fill: 'var(--text-muted)', fontSize: 9 }} label={{ value: 'Cumulative', angle: 90, position: 'insideRight', fill: 'var(--text-muted)', fontSize: 10 }}/>
                      <Tooltip contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', fontSize: 11 }}/>
                      <Legend wrapperStyle={{ fontSize: 11 }}/>
                      <Bar yAxisId="left" dataKey={dailyKey} name={view === 'manpower' ? 'Daily manpower' : 'Daily equipment'} fill="#F26B1F" />
                      <Line yAxisId="right" type="monotone" dataKey={cumKey} name={view === 'manpower' ? 'Cumulative manpower' : 'Cumulative equipment'} stroke="#143A6B" strokeWidth={2} dot={false}/>
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="rounded-xl border p-3" style={{ borderColor: 'var(--border-color)' }}>
                <div className="text-[11px] uppercase tracking-[0.12em] mb-2" style={{ color: 'var(--text-muted)' }}>
                  {view === 'manpower' ? 'Manpower types breakdown' : 'Equipment types breakdown'}
                </div>
                <table className="w-full text-[11px]">
                  <thead style={{ background: 'var(--card-bg-alt)' }}>
                    <tr style={{ color: 'var(--text-muted)' }}>
                      <th className="p-1.5 text-left font-medium">Type</th>
                      <th className="p-1.5 text-right font-medium">{view === 'manpower' ? 'Total person-days' : 'Total equipment-days'}</th>
                      <th className="p-1.5 text-right font-medium">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {typeTotals.map(([type, count]) => {
                      const share = totalPersonDays > 0 ? (count / totalPersonDays * 100).toFixed(1) : 0;
                      return (
                        <tr key={type} className="border-b" style={{ borderColor: 'var(--border-color)' }} data-testid={`swm-rollup-type-${type}`}>
                          <td className="p-1.5" style={{ color: 'var(--text-primary)' }}>{type}</td>
                          <td className="p-1.5 text-right font-mono">{count.toLocaleString()}</td>
                          <td className="p-1.5 text-right font-mono" style={{ color: 'var(--text-muted)' }}>{share}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, color }) {
  return (
    <div className="rounded-xl border p-3" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg)' }}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        <div className="text-[9.5px] uppercase tracking-[0.12em]" style={{ color: 'var(--text-muted)' }}>{label}</div>
      </div>
      <div className="text-xl font-semibold tabular-nums" style={{ color }}>{value}</div>
      {sub && <div className="text-[9.5px] font-mono" style={{ color: 'var(--text-muted)' }}>{sub}</div>}
    </div>
  );
}
